/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

// Node-link shape produced by the Level-2 consolidation (and by the
// catalog-altitude fixture that predates it). Fields beyond these exist on
// the wire; the fleet view only depends on this subset.
export type KgNode = {
  // Synthetic marker: set only on directory-overview level docs (never in
  // store artifacts) so the renderer captions boxes as directories.
  _dir?: boolean;
  id: string;
  label?: string;
  entity_kind?: string; // Component | API | System | Group | ...
  system?: string | null;
  owner?: string | null;
  lifecycle?: string | null;
  tags?: string[];
  // Level-1 (Graphify symbol altitude) fields.
  _callable?: boolean;
  _callable_class?: boolean;
  file_type?: string; // code | concept
  community?: number;
  source_file?: string;
  source_location?: string;
};

export type KgEdge = {
  // Synthetic docs (directory levels, the company graph) key their merged
  // edges; store artifacts don't carry one.
  id?: string;
  source: string;
  target: string;
  // Level-2: consumes | provides | dependsOn | ownedBy | ...
  // Level-1: contains | references | imports | calls | inherits | defines | ...
  relation?: string;
  // declared | observed | both — absent on fixtures that predate the join.
  flag?: 'declared' | 'observed' | 'both';
  // Aggregate size on directory-overview edges (one merged edge per dir
  // pair); absent on symbol edges. Drives stroke width and the panel count.
  count?: number;
  confidence?: string;
};

export type KgDoc = {
  nodes: KgNode[];
  links?: KgEdge[];
  edges?: KgEdge[];
  // Level-1 artifacts stamp the merge commit they were built from.
  built_at_commit?: string;
};

export type KgMeta = {
  loadedAt: string | null;
  source: string | null;
  lastError: string | null;
  staleSeconds: number | null;
  nodeCount: number;
  edgeCount: number;
};

export type GlobalGraphResponse = {
  meta: KgMeta;
  // Small fleet: the whole doc. Giant fleet: summary only — clients drill
  // through the global artifact routes instead (PLAT-1646).
  graph?: KgDoc;
  summary?: KgSummary;
};

export const edgesOf = (doc: KgDoc): KgEdge[] => doc.links ?? doc.edges ?? [];

// The store's `latest` pointer and the artifact's built_at_commit are written
// by different steps and may be abbreviated independently (the backend admits
// 7-40 hex). Prefix-match instead of === so a length mismatch can't read as
// permanent staleness (endless background refetches, sticky "new build" chips).
// (Twin of the backend's shaMatches in extensions/knowledgeGraph.ts — the
// workspaces share no lib, so changes must land in both.)
export const shaMatches = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean =>
  !!a &&
  !!b &&
  a.length >= 7 &&
  b.length >= 7 &&
  (a.startsWith(b) || b.startsWith(a));

// Build-time derived stats (summary.json, emitted next to graph.json):
// cards and the picker read these so no client ever needs the full doc of a
// giant repo. `dirtree: true` marks repos that open with the directory
// overview instead of the symbol renderer.
export type KgSummary = {
  built_at_commit?: string;
  nodes: number;
  edges: number;
  files: number;
  kinds: Record<string, number>;
  top_connected: { label: string; degree: number }[];
  dirtree?: boolean;
};

export type KgDirEntry = {
  path: string;
  name: string;
  parent: string;
  symbols: number;
  files: number;
  children: number;
  shard?: string | null;
};

export type KgDirtree = {
  built_at_commit?: string;
  dirs: KgDirEntry[];
  edges: {
    parent: string;
    source: string;
    target: string;
    family: string;
    count: number;
  }[];
};

// Twin of the backend's RepoStatus in extensions/knowledgeGraph.ts (the
// workspaces share no lib, so changes must land in both). Fleet-health
// status written by lib-graph-refresh beside each repo's store pointer
// (PLAT-1702); served on /repos rows, null until the repo's next build.
export type RepoHealthStatus = {
  outcome:
    | 'published'
    | 'held-pruned'
    | 'held-zero-nodes'
    | 'empty-corpus'
    | 'failed';
  sha?: string | null;
  nodes?: number | null;
  builtAt?: string | null;
};

// Per-repo prefetch entry for the graph-list panel: the doc arrives on idle
// after /repos and its stats double as the "cached" indicator. Giants carry
// a summary instead of a doc.
export type RepoGraphEntry = {
  status: 'pending' | 'loading' | 'ready' | 'error';
  doc?: KgDoc;
  summary?: KgSummary;
  error?: string;
  stats?: { symbols: number; files: number; edges: number; sha?: string };
};
